import { useCallback, useEffect, useMemo, useState } from 'react';
import { clearAuthToken, decodeV8Token, isTokenExpired, readAuthToken } from './auth-token';

export const AUTH_CHANGE_EVENT = 'verse8-auth-change';

export interface AdminAuthState {
  ready: boolean;
  authenticated: boolean;
  account: string | null;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

/**
 * Reads V8 platform JWT from the `verse8_auth_token` cookie, decodes it, and
 * exposes wallet address as `account`. Saving the token is the LoginPage's job
 * (via auth-token.saveAuthToken + dispatching AUTH_CHANGE_EVENT).
 */
export function useAdminAuth(): AdminAuthState {
  const [token, setToken] = useState<string | null>(() => readAuthToken());

  useEffect(() => {
    const handler = () => setToken(readAuthToken());
    window.addEventListener(AUTH_CHANGE_EVENT, handler);
    return () => window.removeEventListener(AUTH_CHANGE_EVENT, handler);
  }, []);

  const decoded = useMemo(() => (token ? decodeV8Token(token) : null), [token]);
  const expired = decoded ? isTokenExpired(decoded) : false;

  useEffect(() => {
    if (token && (decoded === null || expired)) {
      clearAuthToken();
      setToken(null);
    }
  }, [token, decoded, expired]);

  const logout = useCallback(async () => {
    clearAuthToken();
    setToken(null);
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  }, []);

  const getAccessToken = useCallback(
    async () => (token && !expired ? token : null),
    [token, expired]
  );

  return {
    ready: true,
    authenticated: !!token && !expired && !!decoded,
    account: decoded && !expired ? decoded.walletAddress : null,
    logout,
    getAccessToken,
  };
}
